import * as React from 'react'

import { GlobalDispatchAndDateCountdownWidget } from '@thg-commerce/enterprise-graphql/horizon-schema'
import { useTheme } from '@thg-commerce/enterprise-theme'
import { DispatchAndDateCountdown } from '@thg-commerce/enterprise-widget-global-date-and-countdown'
import { TextDecoration } from '@thg-commerce/gravity-theme'

import { BaseWidgetProps } from '../types'

export type GlobalCountDownAndDateType = BaseWidgetProps &
  Omit<BaseWidgetProps, 'widgetType'> &
  Omit<GlobalDispatchAndDateCountdownWidget, 'query'> & {
    targetDate: string
    maxWidth?: string
    textDecoration?: TextDecoration
    displayWeekTimer?: boolean
    weeks: JSX.Element | null
    remainingDays: JSX.Element | null
    hours: JSX.Element | null
    minutes: JSX.Element | null
    seconds: JSX.Element | null
  }

const padWithZero = (num: number) => String(num).padStart(2, '0')

const wrapDigits = (numStr: string) => (
  <React.Fragment>
    <span className="digit">{numStr[0]}</span>
    <span className="digit">{numStr[1]}</span>
  </React.Fragment>
)

const formatTimeCount = (value: number, shouldDisplay: boolean) =>
  shouldDisplay || value > 0 ? wrapDigits(padWithZero(value)) : wrapDigits('00')

const timeDifference = (countDown: number) => {
  if (countDown <= 0) {
    return {
      weeks: wrapDigits('00'),
      remainingDays: wrapDigits('00'),
      hours: wrapDigits('00'),
      minutes: wrapDigits('00'),
      seconds: wrapDigits('00'),
      hasEnded: true,
    }
  }
  const weeksValue = Math.floor(countDown / (1000 * 60 * 60 * 24 * 7))
  const remainingDaysValue = Math.floor(
    (countDown % (1000 * 60 * 60 * 24 * 7)) / (1000 * 60 * 60 * 24),
  )
  const hoursValue = Math.floor(
    (countDown % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
  )
  const minutesValue = Math.floor((countDown % (1000 * 60 * 60)) / (1000 * 60))
  const secondsValue = Math.floor((countDown % (1000 * 60)) / 1000)

  const shouldDisplayDays = weeksValue > 0 || remainingDaysValue > 0
  const shouldDisplayHours = shouldDisplayDays || hoursValue > 0
  const shouldDisplayMinutes = shouldDisplayHours || minutesValue > 0
  const shouldDisplaySeconds = shouldDisplayMinutes || secondsValue > 0

  return {
    weeks:
      weeksValue > 0 || shouldDisplayDays
        ? wrapDigits(padWithZero(weeksValue))
        : wrapDigits('00'),
    remainingDays: formatTimeCount(remainingDaysValue, shouldDisplayDays),
    hours: formatTimeCount(hoursValue, shouldDisplayHours),
    minutes: formatTimeCount(minutesValue, shouldDisplayMinutes),
    seconds: formatTimeCount(secondsValue, shouldDisplaySeconds),
    hasEnded: false,
  }
}

const useCountdown = (targetDate: number) => {
  const countDownDate = new Date(targetDate).getTime()
  const [countDown, setCountDown] = React.useState(
    countDownDate - new Date().getTime(),
  )

  React.useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date().getTime()
      const newCountDown = countDownDate - now

      if (newCountDown >= 0) {
        setCountDown(newCountDown)
      } else {
        setCountDown(0)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [countDownDate])

  return timeDifference(countDown)
}

export const DispatchAndDateCountDownWidgetRenderer = (
  props: GlobalCountDownAndDateType,
) => {
  const theme = useTheme()
  const newTargetDate = new Date(props.targetDate)

  const {
    weeks,
    remainingDays,
    hours,
    minutes,
    seconds,
    hasEnded,
  } = useCountdown(Number(newTargetDate))

  const removeCountDownWidget = props.removeOnEnd === 'true' && hasEnded

  const widgetProps = {
    ...props,
    weeks,
    remainingDays,
    hours,
    minutes,
    seconds,
    textDecoration: theme.widget.dispatchAndDateCountdownWidget?.textDecoration,
    displayWeekTimer:
      theme.widget.dispatchAndDateCountdownWidget.displayWeekTime,
    titleColour: props.titleColour,
    subtitleColour: props.subtitleColour,
    digitsColour: props.digitsColour,
    separatorColour: props.separatorColour,
    backgroundColour: props.backgroundColour,
  }

  return (
    <React.Fragment>
      {!removeCountDownWidget && <DispatchAndDateCountdown {...widgetProps} />}
    </React.Fragment>
  )
}
